import { Button, useTranslation } from '@hbs-us/ui';
import { useMutation } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

import { useAccess } from '../../auth/useAccess.js';
import { useSession } from '../../auth/useSession.js';
import { ApiError, apiFetchJson } from '../../lib/apiFetch.js';

/**
 * "Phone sign-in code" on the employee profile. The office makes a one-time code (8 characters,
 * 10 minutes, single use) and the crew member scans the QR, or types the code, in the phone app.
 * The server ties the code to this one person and this one company, and decides again who may
 * make one; hiding the button here is UX only.
 *
 * Owners can always make a code. Managers can unless the company turned that off
 * (`managersIssueLoginCodes`, default on). The QR is drawn locally, so the code never travels to
 * a third-party generator.
 */

export interface LoginCode {
  code: string;
  qrPayload: string;
  expiresAt: string;
}

/** Owners always; managers unless the company setting says no. Absent setting = the default, yes. */
export function canIssueLoginCode(
  isAdmin: boolean,
  isManager: boolean,
  managersAllowed: boolean | undefined,
): boolean {
  return isAdmin || (isManager && managersAllowed !== false);
}

/** `9:05` for 545 seconds left. Never negative. */
export function formatCountdown(msLeft: number): string {
  const total = Math.max(0, Math.ceil(msLeft / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** A missing route answers with the HTML not-found page, so it has no error code of its own. */
function isMissingRoute(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 404 && error.message === 'request_failed_404'
  );
}

export function PhoneSignInCode({ employeeId }: { employeeId: number }) {
  const { t } = useTranslation();
  const access = useAccess();
  const { session } = useSession();
  const allowed = canIssueLoginCode(
    access({ minRole: 'Admin' }),
    access({ minRole: 'Manager' }),
    session?.companySettings?.managersIssueLoginCodes,
  );

  const [code, setCode] = useState<LoginCode | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [expired, setExpired] = useState(false);

  const path = `/api/v1/employees/${employeeId}/login-code`;
  const mint = useMutation<LoginCode, Error, void>({
    mutationFn: () => apiFetchJson<LoginCode>(path, { method: 'POST' }),
    onSuccess: async (next) => {
      setExpired(false);
      setNow(Date.now());
      setCode(next);
      setQr(
        await QRCode.toDataURL(next.qrPayload, {
          width: 320,
          margin: 1,
          errorCorrectionLevel: 'M',
        }),
      );
    },
  });
  const revoke = useMutation<unknown, Error, void>({
    mutationFn: () => apiFetchJson(path, { method: 'DELETE' }),
    onSuccess: () => {
      setCode(null);
      setQr(null);
    },
  });

  const msLeft = code ? new Date(code.expiresAt).getTime() - now : 0;

  useEffect(() => {
    if (!code) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [code]);

  useEffect(() => {
    if (code && msLeft <= 0) {
      setCode(null);
      setQr(null);
      setExpired(true);
    }
  }, [code, msLeft]);

  // A server without the route yet: the feature is not there, so neither is the card.
  if (!allowed || isMissingRoute(mint.error)) return null;

  const error = mint.error ?? revoke.error;
  const errorMessage =
    error instanceof ApiError
      ? t(`employees:signInCode.errors.${error.message}`, {
          defaultValue: t('employees:signInCode.errors.generic'),
        })
      : error
        ? t('employees:signInCode.errors.generic')
        : null;

  return (
    <section className="rounded-md border border-border bg-card p-6 md:col-span-2">
      <h2 className="mb-1 font-display font-semibold text-foreground text-lg">
        {t('employees:signInCode.heading')}
      </h2>
      <p className="mb-4 max-w-prose text-muted-foreground text-sm">
        {t('employees:signInCode.description')}
      </p>

      {code && qr ? (
        <div className="flex flex-wrap items-start gap-6">
          <img
            src={qr}
            alt={t('employees:signInCode.qrAlt')}
            className="size-48 rounded border border-border bg-white p-2"
          />
          <div className="flex min-w-0 flex-col gap-3">
            <div>
              <p className="font-medium text-muted-foreground text-xs">
                {t('employees:signInCode.typedLabel')}
              </p>
              <p className="font-mono font-semibold text-3xl text-foreground tracking-widest">
                {code.code.slice(0, 4)} {code.code.slice(4)}
              </p>
            </div>
            <p className="text-foreground text-sm">
              {t('employees:signInCode.expiresIn')}{' '}
              <span role="timer" className="font-mono tabular-nums">
                {formatCountdown(msLeft)}
              </span>
            </p>
            <p className="max-w-prose text-muted-foreground text-xs">
              {t('employees:signInCode.singleUse')}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate()}
              >
                {t('employees:signInCode.revoke')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {expired ? (
            <p role="status" className="mb-3 text-muted-foreground text-sm">
              {t('employees:signInCode.expired')}
            </p>
          ) : null}
          <Button type="button" disabled={mint.isPending} onClick={() => mint.mutate()}>
            {t('employees:signInCode.make')}
          </Button>
        </>
      )}

      {errorMessage ? (
        <p role="alert" className="mt-3 text-destructive text-sm">
          {errorMessage}
        </p>
      ) : null}
    </section>
  );
}
